'use client';

import React, { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUp, Briefcase, Building2, Check, FileText, MapPin, Mic, Plus, Tag, X } from 'lucide-react';
import { useAutocomplete } from '../hooks/useAutocomplete';
import { Button } from '@/components/ui/button';
import { LocationAutosuggest, type LocationResult } from '@/components/ui/forms/LocationAutosuggest';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/navigation/dropdown-menu';
import { cn } from '@/lib/utils';
import type { FilterType, SearchFilterGroup } from '../types';

export interface AttachedResumeInfo {
  id: string;
  title: string;
  contentText: string;
}

interface SearchComposerProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  filterGroups?: SearchFilterGroup[];
  onFilterGroupsChange?: (groups: SearchFilterGroup[]) => void;
  attachedResume?: AttachedResumeInfo | null;
  onAddResume?: () => void;
  onRemoveResume?: () => void;
  disabled?: boolean;
  placeholder?: string;
  variant?: 'hero' | 'sticky';
  /** Override the cycling placeholder hints (defaults by variant). */
  placeholderHints?: readonly string[];
  className?: string;
}

type FilterOption = {
  type: FilterType;
  label: string;
  Icon: React.FC<{ className?: string }>;
  placeholder: string;
};

const FILTER_OPTIONS: FilterOption[] = [
  { type: 'company', label: 'Company', Icon: Building2, placeholder: 'e.g. Stripe, Ramp…' },
  { type: 'location', label: 'Location', Icon: MapPin, placeholder: 'City or region…' },
  { type: 'skill', label: 'Skill', Icon: Tag, placeholder: 'e.g. Python, React…' },
  { type: 'title', label: 'Job Title', Icon: Briefcase, placeholder: 'e.g. Senior Engineer…' },
];

const FILTER_LABEL_CLASS: Record<FilterType, string> = {
  company: 'text-blue-600 dark:text-blue-400',
  location: 'text-emerald-600 dark:text-emerald-400',
  skill: 'text-purple-600 dark:text-purple-400',
  title: 'text-amber-600 dark:text-amber-400',
};

/* ---------- typing-cycle hook -------------------------------------------- */

const HERO_HINTS = [
  'Software engineer jobs in New York…',
  'Marketing managers at tech startups…',
  'Remote product designer roles…',
  'Sales leaders at mid-size companies…',
  'Frontend developer jobs in San Francisco…',
  'HR managers hiring in Chicago…',
] as const;

const REFINE_HINTS = [
  'Refine your search or ask a question…',
  'Ask about salary, culture, or office locations…',
  'Narrow down by company or location…',
  'Try a different role or skill…',
  'Ask a follow-up question…',
] as const;

function useWordCycle(words: readonly string[], holdMs = 3500) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % words.length), holdMs);
    return () => clearInterval(id);
  }, [words, holdMs]);
  return words[index];
}

/* ---------- main composer ------------------------------------------------- */

const MAX_VISIBLE_LINES = 4;

export function SearchComposer({
  value,
  onChange,
  onSubmit,
  filterGroups = [],
  onFilterGroupsChange,
  attachedResume,
  onAddResume,
  onRemoveResume,
  disabled,
  placeholder = 'Ask anything — a role, a city, a kind of company...',
  variant = 'hero',
  placeholderHints,
  className,
}: SearchComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [addingType, setAddingType] = useState<FilterType | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [cursorAtEnd, setCursorAtEnd] = useState(true);
  const cycledHint = useWordCycle(
    placeholderHints ?? (variant === 'sticky' ? REFINE_HINTS : HERO_HINTS),
  );
  const { ghost, accept, clear } = useAutocomplete(value);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '20');
    const maxHeight = lineHeight * MAX_VISIBLE_LINES + 8;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value]);

  function handleKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.key === 'Tab' || event.key === 'ArrowRight') && ghost && cursorAtEnd) {
      event.preventDefault();
      onChange(accept());
      return;
    }
    if (event.key === 'Escape' && ghost) {
      clear();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if ((value.trim() || attachedResume) && !disabled) onSubmit();
    }
  }

  function startAdding(type: FilterType) {
    setAddingType(type);
    setDraftValue('');
    if (!filterGroups.find((g) => g.type === type)) {
      onFilterGroupsChange?.([...filterGroups, { type, values: [], autoValues: [] }]);
    }
  }

  function confirmValue(type: FilterType, val: string, autoClose = false) {
    const trimmed = val.trim();
    if (!trimmed) return;

    const existing = filterGroups.find((g) => g.type === type);
    if (existing?.values.includes(trimmed)) {
      setDraftValue('');
      return;
    }

    onFilterGroupsChange?.(
      filterGroups.map((g) =>
        g.type === type ? { ...g, values: [...g.values, trimmed] } : g,
      ),
    );
    setDraftValue('');
    if (autoClose) setAddingType(null);
  }

  function removeValue(type: FilterType, val: string) {
    onFilterGroupsChange?.(
      filterGroups
        .map((g) => (g.type === type ? { ...g, values: g.values.filter((v) => v !== val) } : g))
        .filter((g) => g.values.length > 0 || g.autoValues.length > 0),
    );
  }

  function removeAutoValue(type: FilterType, val: string) {
    onFilterGroupsChange?.(
      filterGroups
        .map((g) => (g.type === type ? { ...g, autoValues: g.autoValues.filter((v) => v !== val) } : g))
        .filter((g) => g.values.length > 0 || g.autoValues.length > 0),
    );
  }

  function cancelAdding() {
    onFilterGroupsChange?.(filterGroups.filter((g) => g.values.length > 0 || g.autoValues.length > 0));
    setAddingType(null);
    setDraftValue('');
  }

  const hasFiltersArea =
    filterGroups.some((g) => g.values.length > 0 || g.autoValues.length > 0) ||
    addingType !== null ||
    Boolean(attachedResume);

  return (
    <div
      className={cn(
        'group/composer w-full rounded-xl border border-border bg-card text-left transition-shadow',
        'focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/40',
        variant === 'hero' ? 'p-2 sm:p-3 shadow-warm-sm' : 'p-2',
        className,
      )}
      style={{
        boxShadow: variant === 'hero' ? 'var(--shadow-warm-sm)' : undefined,
      }}
    >
      <div className="relative">
        {/* Ghost text mirror — desktop only, sits behind textarea */}
        {value && ghost && cursorAtEnd && (
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 hidden overflow-hidden whitespace-pre-wrap break-words px-2 py-1 font-medium sm:block',
              variant === 'hero' ? 'text-[15px]' : 'text-[13px]',
            )}
          >
            <span style={{ visibility: 'hidden' }}>{value}</span>
            <span className="text-muted-foreground/40">{ghost}</span>
          </div>
        )}

        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          onKeyDown={handleKey}
          onSelect={(e) => {
            const el = e.target as HTMLTextAreaElement;
            setCursorAtEnd(el.selectionEnd === el.value.length);
          }}
          placeholder=""
          disabled={disabled}
          className={cn(
            'relative block w-full resize-none border-0 bg-transparent px-2 py-1 font-medium text-foreground',
            'focus:outline-none focus:ring-0',
            variant === 'hero' ? 'text-[15px]' : 'text-[13px]',
          )}
        />

        {/* Slide-up animated placeholder — only shown when empty */}
        {!value && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden px-2 py-1"
          >
            <span
              key={cycledHint}
              className={cn(
                'block font-medium text-muted-foreground/55',
                variant === 'hero' ? 'text-[15px]' : 'text-[13px]',
              )}
              style={{ animation: 'search-hint-in 0.4s ease-out' }}
            >
              {cycledHint}
            </span>
          </div>
        )}

        {/* Desktop: subtle Tab badge */}
        {value && ghost && cursorAtEnd && (
          <button
            type="button"
            aria-label="Accept suggestion"
            onClick={() => onChange(accept())}
            className={cn(
              'absolute right-1 top-1/2 -translate-y-1/2',
              'hidden sm:flex items-center gap-1 rounded-full bg-muted/80 px-2 py-0.5',
              'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground',
              'transition-opacity hover:text-foreground',
            )}
          >
            <ArrowRight className="h-3 w-3" />
            Tab
          </button>
        )}
      </div>

      {/* Mobile suggestion chip — tap to accept, hidden on desktop */}
      {value && ghost && cursorAtEnd && (
        <button
          type="button"
          aria-label="Accept suggestion"
          onClick={() => onChange(accept())}
          className={cn(
            'sm:hidden mt-1 flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5',
            'bg-muted/40 text-left text-muted-foreground active:bg-muted/70',
          )}
        >
          <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
          <span className={cn('truncate font-medium', variant === 'hero' ? 'text-[15px]' : 'text-[13px]')}>
            {value}
            <span className="text-muted-foreground/50">{ghost}</span>
          </span>
        </button>
      )}

      {/* Filter chips — flat strip, icon-prefixed */}
      {hasFiltersArea && (
        <div className="flex flex-wrap items-center gap-1.5 px-2 pt-1.5 pb-0">
          {/* Resume chip */}
          {attachedResume && (
            <span className="inline-flex h-7 items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 text-xs text-foreground">
              <FileText className="h-3 w-3 flex-shrink-0 text-primary/70" />
              <span className="truncate max-w-[180px]">
                {attachedResume.title.length > 28
                  ? `${attachedResume.title.slice(0, 27).trimEnd()}...`
                  : attachedResume.title}
              </span>
              <button
                type="button"
                aria-label={`Remove resume ${attachedResume.title}`}
                onClick={onRemoveResume}
                className="ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}

          {filterGroups.flatMap((group) => {
            const opt = FILTER_OPTIONS.find((o) => o.type === group.type)!;
            const manualChips = group.values.map((val) => (
              <span
                key={`${group.type}-m-${val}`}
                className="inline-flex h-7 items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 text-xs text-foreground"
              >
                <opt.Icon className={cn('h-3 w-3 flex-shrink-0', FILTER_LABEL_CLASS[group.type])} />
                {val}
                <button
                  type="button"
                  aria-label={`Remove ${val}`}
                  onClick={() => removeValue(group.type, val)}
                  className="ml-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ));

            const autoChips = group.autoValues.map((val) => (
              <span
                key={`${group.type}-a-${val}`}
                className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-primary/30 bg-primary/5 px-2.5 text-xs text-foreground/75"
              >
                <opt.Icon className={cn('h-3 w-3 flex-shrink-0 opacity-60', FILTER_LABEL_CLASS[group.type])} />
                {val}
                <button
                  type="button"
                  aria-label={`Remove ${val}`}
                  onClick={() => removeAutoValue(group.type, val)}
                  className="ml-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ));

            // autoChips hidden for now — re-enable when auto-detected filters are useful
            return manualChips;
          })}

          {/* Inline filter input — shown when actively adding a value */}
          {addingType && (
            <FilterValueInput
              type={addingType}
              value={draftValue}
              onChange={(val, loc) => {
                setDraftValue(val);
                if (loc) confirmValue(addingType, val, true);
              }}
              onConfirm={() => confirmValue(addingType, draftValue, true)}
              onCancel={cancelAdding}
            />
          )}
        </div>
      )}

      {/* Bottom toolbar */}
      <div className="mt-1.5 flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          {/* Resume button — left */}
          {onAddResume && !attachedResume && (
            <button
              type="button"
              aria-label="Attach resume"
              onClick={onAddResume}
              className={cn(
                'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-3',
                'border border-primary bg-transparent text-primary transition-colors',
                'hover:bg-primary/8',
              )}
            >
              <FileText className="h-3.5 w-3.5" />
              <span className="text-[11px] font-bold hidden sm:inline">Resume</span>
            </button>
          )}

          {/* Filters button — right of resume */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <AnimatedFilterTrigger />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <div className="px-2 pt-1.5 pb-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/70">
                  Filter by
                </p>
              </div>
              {FILTER_OPTIONS.map(({ type, label, Icon }) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => startAdding(type)}
                >
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-1.5">
          <ChromeIconButton ariaLabel="Voice input (coming soon)" disabled>
            <Mic className="h-4 w-4" />
          </ChromeIconButton>
          <Button
            type="button"
            size="icon"
            disabled={!(value.trim().length > 0 || attachedResume) || disabled}
            onClick={onSubmit}
            aria-label="Submit search"
            className="h-9 w-9 rounded-full"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FilterValueInput — inline text or location input for adding a value       */
/* -------------------------------------------------------------------------- */

function FilterValueInput({
  type,
  value,
  onChange,
  onConfirm,
  onCancel,
}: {
  type: FilterType;
  value: string;
  onChange: (val: string, loc?: LocationResult) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (type !== 'location') {
      // Delay matches LookupFilterCard — lets the dropdown fully close and
      // release its focus trap before we steal focus.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [type]);

  if (type === 'location') {
    return (
      <div className="flex items-center gap-1">
        <div className="w-64 sm:w-80">
          <LocationAutosuggest
            value={value}
            onChange={(val, loc) => onChange(val, loc)}
            showCountrySelector
            placeholder="City or region…"
            inputClassName="h-7 py-0 text-xs"
          />
        </div>
        <button
          type="button"
          onClick={() => onConfirm()}
          disabled={!value.trim()}
          aria-label="Confirm location"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-30 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  const opt = FILTER_OPTIONS.find((o) => o.type === type)!;

  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-primary/40 bg-primary/5 pl-2 pr-0.5">
      <opt.Icon className="h-3 w-3 flex-shrink-0 text-primary/70" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) { e.preventDefault(); onConfirm(); }
          if (e.key === 'Escape') onCancel();
        }}
        placeholder={opt.placeholder}
        className="h-6 w-32 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
      />
      <button
        type="button"
        onClick={onConfirm}
        disabled={!value.trim()}
        aria-label="Confirm"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-30 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
      >
        <Check className="h-2.5 w-2.5" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancel"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  AnimatedFilterTrigger — + pill with typewriter hint cycling               */
/* -------------------------------------------------------------------------- */

const AnimatedFilterTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label="Add filter"
    {...props}
    className={cn(
      'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2.5',
      'text-muted-foreground/50 transition-colors',
      'hover:bg-muted/40 hover:text-muted-foreground',
      props.className,
    )}
  >
    <Plus className="h-3.5 w-3.5" />
    <span className="text-[11px] font-medium hidden sm:inline">Filters</span>
  </button>
));
AnimatedFilterTrigger.displayName = 'AnimatedFilterTrigger';

/* -------------------------------------------------------------------------- */
/*  ChromeIconButton — generic disabled-aware icon button                    */
/* -------------------------------------------------------------------------- */

function ChromeIconButton({
  children,
  ariaLabel,
  disabled,
}: {
  children: React.ReactNode;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      title={disabled ? 'Coming soon' : ariaLabel}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors',
        'hover:bg-muted hover:text-foreground',
        disabled && 'opacity-60',
      )}
    >
      {children}
    </button>
  );
}
